/**
 * Bridge from the in-app chat telemetry seam (`telemetry.ts` `trackClientEvent`)
 * to product analytics. The chat surfaces already emit `ChatTelemetryEvent`s; this
 * registers the single handler that maps the three product-relevant ones to the
 * typed analytics wrappers, dropping everything that must never leave the client
 * (the user's message text, the raw error string).
 */
import type { ChatTelemetryEvent } from "../telemetry";
import { setTelemetryHandler } from "../telemetry";
import type { ChatErrorType } from "./events";
import { trackChatErrorShown, trackChatMessageSent, trackChatResponseReceived } from "./segment";

/**
 * Classify a raw chat error message into a bounded category. The raw string is
 * unbounded free text (potential content), so analytics never sees it — only this
 * enum. Keyword rules are deliberate and ordered; anything unmatched is `unknown`.
 */
export function classifyChatError(details?: string): ChatErrorType {
  if (!details) {
    return "unknown";
  }
  const text = details.toLowerCase();
  if (text.includes("capacity")) {
    return "capacity";
  }
  if (text.includes("timeout") || text.includes("timed out")) {
    return "timeout";
  }
  if (
    text.includes("disconnect") ||
    text.includes("network") ||
    text.includes("connection") ||
    text.includes("offline")
  ) {
    return "network";
  }
  if (text.includes("without returning content") || text.includes("no content")) {
    return "empty_response";
  }
  return "unknown";
}

/**
 * Map a chat telemetry event to a product-analytics event. Only the three
 * product-relevant events are forwarded; the rest (warnings, thread management,
 * first-token timing) are not product analytics and are ignored.
 */
export function handleChatTelemetry(event: ChatTelemetryEvent): void {
  switch (event.name) {
    case "chat_send":
      // `message` (the user's question text) is intentionally never forwarded.
      trackChatMessageSent({ surface: event.surface, threadId: event.threadId ?? undefined });
      return;
    case "chat_success":
      trackChatResponseReceived({
        surface: event.surface,
        threadId: event.threadId,
        latencyMs: event.latencyMs,
        promptTokens: event.tokenUsage?.prompt,
        completionTokens: event.tokenUsage?.completion,
        hadCitations: event.hadCitations,
        citationCount: event.citationCount,
      });
      return;
    case "chat_error":
      // The raw `details` is classified to a bounded enum and then dropped.
      trackChatErrorShown({
        surface: event.surface,
        threadId: event.threadId,
        errorType: classifyChatError(event.details),
      });
      return;
    default:
      return;
  }
}

/** Register the analytics bridge as the chat telemetry handler. Call once at startup. */
export function registerChatTelemetry(): void {
  setTelemetryHandler(handleChatTelemetry);
}
